"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

import {
  THEATER_ELIGIBLE_ATTR,
  THEATER_ROOT_STATE_ATTR,
  THEATER_STATE_ATTR,
} from "@/lib/virtualspace/public-virtual-space-theater";

/**
 * Theater-mode controller for public VirtualSpace renderer.
 *
 * Watches every `[data-vs-theater-eligible="true"]` element inside a given
 * scroll root and, while scrolling, picks the element that is **most
 * centered** in the viewport — provided it covers ≥ 50% of its own height
 * and falls inside the middle 70% band of the scroll container. That winner
 * gets `data-vs-theater="active"` (block-level) and the scroll root gets
 * `data-vs-theater-active="1"` so global CSS can dim non-media siblings and
 * fade the spaces rail.
 *
 * Manual override:
 * - Each media block can call `setManualTheater(blockKey)` (from the in-block
 *   "T" / Maximise toggle) to force theater on a specific block regardless
 *   of scroll position.
 * - Pressing Escape clears the manual override.
 *
 * Reduced motion:
 * - When `prefers-reduced-motion: reduce`, we still flip the theater state
 *   (so width grows) but skip the dim animation duration via CSS — see
 *   `globals.css` `.vs-theater-*` rules.
 */

type TheaterMode = "auto" | "manual";

type TheaterContextValue = {
  /** Currently active block key (typically the block id), or null. */
  activeKey: string | null;
  /** Whether the active state is operator scrolling (auto) or pinned (manual). */
  mode: TheaterMode;
  /**
   * Force theater on a specific block. Pass `null` to clear. Subsequent
   * scrolls won't override until the operator clears manual via Escape or
   * the same toggle.
   */
  setManualTheater: (key: string | null) => void;
  /** Convenience: the active block toggled itself. */
  toggleManualTheater: (key: string) => void;
};

const TheaterContext = createContext<TheaterContextValue>({
  activeKey: null,
  mode: "auto",
  setManualTheater: () => {},
  toggleManualTheater: () => {},
});

/** Inside a media block, read whether *this* block is currently in theater. */
export function useTheaterStateFor(blockKey: string): {
  active: boolean;
  manual: boolean;
  toggle: () => void;
} {
  const ctx = useContext(TheaterContext);
  return {
    active: ctx.activeKey === blockKey,
    manual: ctx.activeKey === blockKey && ctx.mode === "manual",
    toggle: () => ctx.toggleManualTheater(blockKey),
  };
}

type ProviderProps = {
  /** The scroll container ref — `[data-vs-theater-eligible]` is queried inside this. */
  scrollRootRef: RefObject<HTMLElement | null>;
  /** Disabled? (e.g. SSR / no IO support / no media blocks.) Falls through. */
  disabled?: boolean;
  children: ReactNode;
};

/**
 * Mounts the IntersectionObserver and key handler. Apply `data-vs-theater-active`
 * to the scroll root when an active block exists so descendant CSS can fade
 * non-media content + rail.
 */
export function VirtualSpaceTheaterProvider({
  scrollRootRef,
  disabled,
  children,
}: ProviderProps) {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [mode, setMode] = useState<TheaterMode>("auto");
  const modeRef = useRef<TheaterMode>("auto");
  modeRef.current = mode;

  const setManualTheater = useCallback((key: string | null) => {
    if (key === null) {
      setMode("auto");
      setActiveKey(null);
      return;
    }
    setMode("manual");
    setActiveKey(key);
  }, []);

  const toggleManualTheater = useCallback((key: string) => {
    setActiveKey((prev) => {
      const wasActive = prev === key && modeRef.current === "manual";
      setMode(wasActive ? "auto" : "manual");
      return wasActive ? null : key;
    });
  }, []);

  // Observe theater-eligible blocks against the viewport.
  //
  // Post-2026-05-07 the page uses BODY scroll, so the IntersectionObserver
  // always observes against the viewport (`root: null`). The
  // `scrollRootRef` is now purely a "marker" the controller uses to scope
  // queries + project state attributes; it's not a scroll container and
  // its scroll event never fires. Window scroll is the trigger.
  useEffect(() => {
    if (disabled) return;
    if (typeof window === "undefined") return;
    if (typeof IntersectionObserver === "undefined") return;

    let frame = 0;
    const ratios = new Map<Element, number>();

    const recompute = () => {
      // Only auto-mode reacts to scroll; manual stays pinned.
      if (modeRef.current === "manual") return;

      // Centred score: how close is the element to the viewport's
      // vertical centre? Combined with the IO ratio so a block has to
      // be ≥ 50 % visible AND near the middle to qualify.
      const centerY = window.innerHeight / 2;

      let bestKey: string | null = null;
      let bestScore = 0;
      for (const [el, ratio] of ratios) {
        if (ratio < 0.5) continue;
        const r = el.getBoundingClientRect();
        const elCenter = r.top + r.height / 2;
        const distance = Math.abs(elCenter - centerY);
        // Closeness in [0..1] — perfectly centered = 1, half-viewport away = 0.
        const closeness = Math.max(
          0,
          1 - distance / Math.max(window.innerHeight / 2, 1),
        );
        const score = ratio * 0.6 + closeness * 0.4;
        if (score > bestScore) {
          bestScore = score;
          bestKey = el.getAttribute(THEATER_ELIGIBLE_ATTR);
          if (bestKey === "true" || bestKey === null) {
            bestKey = (el as HTMLElement).dataset.vsTheaterKey ?? null;
          }
        }
      }

      setActiveKey((prev) => (prev === bestKey ? prev : bestKey));
    };

    const scheduleRecompute = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(recompute);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            ratios.set(entry.target, entry.intersectionRatio);
          } else {
            ratios.delete(entry.target);
          }
        }
        scheduleRecompute();
      },
      {
        // Viewport — page uses body scroll.
        root: null,
        threshold: [0, 0.25, 0.5, 0.7, 0.85, 1],
        rootMargin: "-15% 0px -15% 0px",
      },
    );

    // Use the rail's marker wrapper to scope our query when present;
    // fall through to document for callers without a marker (rare).
    const queryRoot: ParentNode = scrollRootRef.current ?? document;

    const eligible = queryRoot.querySelectorAll(
      `[${THEATER_ELIGIBLE_ATTR}="true"]`,
    );
    eligible.forEach((el) => observer.observe(el));

    // Re-observe if blocks mount asynchronously (lazy-loaded viewers
    // swap skeletons for canvases — element identity stays the same
    // wrapper).
    const mo = new MutationObserver(() => {
      const next = queryRoot.querySelectorAll(
        `[${THEATER_ELIGIBLE_ATTR}="true"]`,
      );
      const seen = new Set(Array.from(ratios.keys()));
      next.forEach((el) => {
        if (!seen.has(el)) observer.observe(el);
      });
    });
    const moTarget =
      scrollRootRef.current ?? document.body ?? document.documentElement;
    mo.observe(moTarget, { childList: true, subtree: true });

    window.addEventListener("scroll", scheduleRecompute, { passive: true });
    window.addEventListener("resize", scheduleRecompute);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      mo.disconnect();
      window.removeEventListener("scroll", scheduleRecompute);
      window.removeEventListener("resize", scheduleRecompute);
    };
  }, [disabled, scrollRootRef]);

  // Escape clears manual theater pinning.
  useEffect(() => {
    if (disabled) return;
    if (typeof window === "undefined") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (modeRef.current !== "manual") return;
      setMode("auto");
      setActiveKey(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [disabled]);

  // Project the active key onto the scroll root + active block as data
  // attrs. Also mirror onto <html> via `data-vs-theater-root` so the
  // spaces rail — which is a fixed-positioned sibling outside this
  // scroll root — can fade via a global CSS rule without prop drilling.
  //
  // Post-2026-05-07 the scrollRoot is no longer a real scroll container
  // (the page uses body scroll), but the rail's outer wrapper still
  // carries the ref so we have a stable element to project the
  // root-state attribute onto AND a scoped container to query for
  // theater-eligible descendants. When the ref is null (e.g. a future
  // caller that doesn't render the rail at all) we fall through to
  // `document.body` for the same effect.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = scrollRootRef.current ?? document.body;
    const docEl = document.documentElement;
    if (activeKey) {
      root.setAttribute(THEATER_ROOT_STATE_ATTR, "1");
      docEl.setAttribute("data-vs-theater-root", "1");
    } else {
      root.removeAttribute(THEATER_ROOT_STATE_ATTR);
      docEl.removeAttribute("data-vs-theater-root");
    }
    // Mark the winner block with state="active" and clear all others.
    const all = root.querySelectorAll(`[${THEATER_ELIGIBLE_ATTR}="true"]`);
    all.forEach((el) => {
      const key = (el as HTMLElement).dataset.vsTheaterKey;
      if (activeKey && key === activeKey) {
        el.setAttribute(THEATER_STATE_ATTR, "active");
      } else {
        el.removeAttribute(THEATER_STATE_ATTR);
      }
    });
    return () => {
      docEl.removeAttribute("data-vs-theater-root");
    };
  }, [activeKey, scrollRootRef]);

  const value = useMemo<TheaterContextValue>(
    () => ({ activeKey, mode, setManualTheater, toggleManualTheater }),
    [activeKey, mode, setManualTheater, toggleManualTheater],
  );

  return (
    <TheaterContext.Provider value={value}>{children}</TheaterContext.Provider>
  );
}
